// Passengers, pickups, drop-offs, fare maths. Host-authoritative in multiplayer; pure data here.

import { Rng } from './rng';
import type { Ground } from './ground';

export interface Landmark { name: string; p: [number, number, number]; }
export interface PickupPoint { id: number; x: number; z: number; label: string; }

export const PASSENGERS = [
  { id: 'kid', name: 'Kid', colour: 'FF7A1A', lines: ['Are we there yet?', 'Faster! FASTER!', 'Can we honk? Please?'] },
  { id: 'grandma', name: 'Grandma', colour: 'FF6BB5', lines: ['Slow down, dear.', 'In my day taxis had manners.', 'Mind the pedestrians!'] },
  { id: 'business', name: 'Businessperson', colour: '3E8BFF', lines: ['I have a meeting in four minutes.', 'Take the bridge!', 'Do you accept expenses?'] },
  { id: 'dog', name: 'Dog in a hat', colour: 'F2B90C', lines: ['Woof.', 'Woof woof!', 'Window. Down. Now.'] },
  { id: 'tourist', name: 'Tourist', colour: '4CC26B', lines: ['Is that the famous volcano?', 'Ooh, a stadium!', 'Photo stop? No? Okay.'] },
  { id: 'chef', name: 'Chef', colour: 'E5433A', lines: ['The soufflé will not wait!', 'Left! No, the other left!', 'You drive like my sous-chef.'] },
  { id: 'hero', name: 'Superhero', colour: '9B5DE5', lines: ['The city needs me. Step on it.', 'I could fly, but the cape is at the cleaners.', 'Nice cornering, citizen.'] },
  { id: 'robot', name: 'Robot', colour: 'B8BEC9', lines: ['ROUTE COMPUTED. YOURS IS WORSE.', 'BEEP.', 'DESTINATION IMMINENT.'] },
] as const;
export type PassengerKind = (typeof PASSENGERS)[number]['id'];

export interface Fare {
  id: number;
  passenger: PassengerKind;
  pickup: PickupPoint;
  dest: { name: string; x: number; z: number };
  /** total seconds allowed once picked up */
  timeAllowed: number;
  base: number;
  state: 'waiting' | 'riding' | 'done' | 'failed';
  /** id of the player carrying them */
  rider: string | null;
  timeLeft: number;
  collisions: number;
}

export const FARE = {
  base: 20,
  perTenMetres: 3,
  speedTipMax: 0.5,
  goodTip: 0.2,
  smoothBonus: 10,
  rushMultiplier: 2,
  pickupRadius: 4.5,
  dropRadius: 6,
  /** expected cruising speed used to size the timer */
  cruise: 11,
  timerSlack: 1.7,
  timerExtra: 5,
} as const;

/** Build pickup points: bus stops plus the road cell nearest each landmark. */
export function buildPickupPoints(ground: Ground, landmarks: Landmark[], busStops: { p: [number, number, number] }[]): PickupPoint[] {
  const pts: PickupPoint[] = []; let id = 1;
  const seen = new Set<string>();
  const add = (x: number, z: number, label: string) => {
    const r = ground.nearestRoad(x, z, 40); if (!r) return;
    const key = `${Math.round(r.x / 5)}:${Math.round(r.z / 5)}`; if (seen.has(key)) return; seen.add(key);
    pts.push({ id: id++, x: r.x, z: r.z, label });
  };
  for (const b of busStops) add(b.p[0], b.p[2], 'Bus stop');
  for (const l of landmarks) add(l.p[0], l.p[2], l.name);
  return pts;
}

/** Landmark destinations snapped to the nearest road so the drop ring is reachable. */
export function buildDestinations(ground: Ground, landmarks: Landmark[]): { name: string; x: number; z: number }[] {
  const out: { name: string; x: number; z: number }[] = []; const seen = new Set<string>();
  for (const l of landmarks) {
    const base = l.name.replace(/ \d+$/, '');
    const r = ground.nearestRoad(l.p[0], l.p[2], 40); if (!r) continue;
    const key = `${base}:${Math.round(r.x / 20)}:${Math.round(r.z / 20)}`; if (seen.has(key)) continue; seen.add(key);
    out.push({ name: base, x: r.x, z: r.z });
  }
  return out;
}

export function createFare(rng: Rng, id: number, pickups: PickupPoint[], dests: { name: string; x: number; z: number }[], near?: { x: number; z: number; minDist: number; maxDist: number }): Fare {
  let pickup = rng.pick(pickups);
  if (near) {
    const ok = pickups.filter((p) => { const d = Math.hypot(p.x - near.x, p.z - near.z); return d >= near.minDist && d <= near.maxDist; });
    if (ok.length) pickup = rng.pick(ok);
  }
  // destination: not too close, not absurdly far
  const cands = dests.filter((d) => { const dist = Math.hypot(d.x - pickup.x, d.z - pickup.z); return dist > 90 && dist < 520; });
  const dest = rng.pick(cands.length ? cands : dests);
  const dist = Math.hypot(dest.x - pickup.x, dest.z - pickup.z);
  const timeAllowed = Math.round((dist * 1.35 / FARE.cruise) * FARE.timerSlack + FARE.timerExtra); // 1.35 road-vs-crow factor
  return { id, passenger: rng.pick(PASSENGERS).id, pickup, dest, timeAllowed, base: FARE.base + Math.round(dist / 10) * FARE.perTenMetres, state: 'waiting', rider: null, timeLeft: timeAllowed, collisions: 0 };
}

export function farePayout(f: Fare, opts: { karma: number; rush: boolean; tipBonus: number }): { total: number; tip: number; smooth: number } {
  const frac = Math.max(0, f.timeLeft / f.timeAllowed);
  const speedTip = frac > 0.5 ? FARE.speedTipMax : FARE.speedTipMax * (frac / 0.5);
  const goodTip = opts.karma > 30 ? FARE.goodTip : 0;
  let total = f.base * (1 + speedTip + goodTip + opts.tipBonus);
  const smooth = f.collisions === 0 ? FARE.smoothBonus : 0;
  total += smooth;
  if (opts.rush) total *= FARE.rushMultiplier;
  return { total: Math.round(total), tip: Math.round(f.base * (speedTip + goodTip)), smooth };
}
